import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import { registerAdminUser, forceRegisterAdmin } from '../utils/dynamicAdminManager';
import { autoDetectAndConfigureAdmin } from '../utils/autoAdminDetection';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sign up with email and password
  const signup = async (email, password, userData) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Create user document in Firestore
    await setDoc(doc(db, 'users', user.uid), {
      email: user.email,
      role: userData.role || 'client',
      createdAt: new Date(),
      phone: userData.phone || '', // Initialize empty for onboarding check
      address: userData.address || '', // Initialize empty for onboarding check
      profileCompleted: false, // Track if onboarding is completed
      ...userData
    });
    
    return userCredential;
  };

  // Sign in with email and password
  const login = (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  // Sign in with Google
  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    const user = result.user;
    
    // Check if user document exists, if not create one
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (!userDoc.exists()) {
      await setDoc(doc(db, 'users', user.uid), {
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        role: 'client', // Default role for Google sign-in
        phone: '', // Initialize empty for onboarding check
        address: '', // Initialize empty for onboarding check
        profileCompleted: false, // Track if onboarding is completed
        createdAt: new Date()
      });
    }
    
    return result;
  };

  // Sign in with Google as Admin
  const signInWithGoogleAsAdmin = async () => {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    const user = result.user;
    
    // Check if user document exists, if not create one with admin role
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (!userDoc.exists()) {
      await setDoc(doc(db, 'users', user.uid), {
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        role: 'admin', // Admin role for admin Google sign-in
        isAdmin: true,
        createdAt: new Date()
      });
    }
    
    return result;
  };

  // Sign out
  const logout = () => {
    return signOut(auth);
  };

  // Reset password
  const resetPassword = (email) => {
    return sendPasswordResetEmail(auth, email);
  };

  // Get user role from Firestore
  const getUserRole = async (uid) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        return userDoc.data().role;
      }
      return 'client'; // Default role
    } catch (error) {
      console.error('Error getting user role:', error);
      return 'client';
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Obtener datos del usuario
          const userRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setCurrentUser(user);
            setUserRole(userData.role || 'client');
            
            // IMPORTANTE: Detectar y configurar automáticamente si es administrador
            const isAdmin = await autoDetectAndConfigureAdmin(user);
            
            // Si es administrador, registrarlo para notificaciones
            if (isAdmin || userData.role === 'admin' || userData.isAdmin === true) {
              console.log('🔑 Detectado usuario administrador. Registrando para notificaciones...');
              await registerAdminUser(user.uid, userData);
            }
            
            // Compatibilidad con usuarios que ya tienen ID de administrador hardcodeado
            if (user.uid === 'JlSANylhk7YUCSX5wj9sfuYRf6B3') {
              console.log('⭐ Detectado administrador principal. Registro garantizado.');
              forceRegisterAdmin(user.uid);
            }
          } else {
            // Usuario no tiene documento, verificar si debería ser administrador
            const isAdmin = await autoDetectAndConfigureAdmin(user);
            setCurrentUser(user);
            setUserRole(isAdmin ? 'admin' : 'client');
            
            if (isAdmin) {
              console.log('🔑 Nuevo usuario detectado como administrador y configurado automáticamente');
              await registerAdminUser(user.uid, { role: 'admin', isAdmin: true });
            }
          }
        } catch (error) {
          console.error('Error procesando datos de usuario:', error);
          setCurrentUser(user);
          setUserRole('client');
        }
      } else {
        setCurrentUser(null);
        setUserRole(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userRole,
    signup,
    login,
    logout,
    resetPassword,
    signInWithGoogle,
    signInWithGoogleAsAdmin,
    getUserRole
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
